import { useEffect, useState } from "react";

export type SupplierBillStatus = "DRAFT" | "POSTED" | "VOIDED";

export interface SupplierBillRecord {
  id: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate?: string;
  supplierId: string;
  goodsReceiptId?: string;
  subtotalPaise: string;
  discountPaise: string;
  freightPaise: string;
  handlingPaise: string;
  otherChargesPaise: string;
  taxPaise: string;
  tcsPaise: string;
  roundingPaise: string;
  totalPaise: string;
  status: SupplierBillStatus;
  supplier?: { id: string; name: string; phone?: string; email?: string };
  goodsReceipt?: {
    id: string;
    grnNumber: string;
    grnDate: string;
    status: string;
  };
  lines: Array<{
    id: string;
    sourceLineId: string;
    quantityMilli: string;
    unitCostPaise: string;
    discountPaise?: string;
    taxablePaise: string;
    taxPaise: string;
    totalPaise: string;
    rateBps: number;
    product?: { id: string; name: string; sku?: string };
  }>;
}

const STATUS_LABELS: Record<SupplierBillStatus, string> = {
  DRAFT: "Draft",
  POSTED: "Posted",
  VOIDED: "Voided",
};

const STATUS_COLORS: Record<SupplierBillStatus, string> = {
  DRAFT: "bg-yellow-100 text-yellow-800",
  POSTED: "bg-green-100 text-green-800",
  VOIDED: "bg-red-100 text-red-800",
};

function money(paise: string | number) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(paise || 0) / 100);
}

function date(value?: string) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function requestError(error: unknown, fallback: string) {
  const candidate = error as {
    response?: { data?: { message?: string | string[] } };
    message?: string;
  };
  const message = candidate.response?.data?.message;
  return Array.isArray(message)
    ? message.join(", ")
    : message || candidate.message || fallback;
}

export function SupplierBillsListView({
  loadBills,
  onOpen,
  onReceiveStock,
}: {
  loadBills: (params?: {
    status?: SupplierBillStatus;
  }) => Promise<SupplierBillRecord[]>;
  onOpen: (id: string) => void;
  onReceiveStock: () => void;
}) {
  const [bills, setBills] = useState<SupplierBillRecord[]>([]);
  const [status, setStatus] = useState<SupplierBillStatus | "">("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    loadBills(status ? { status } : undefined)
      .then((records) => active && setBills(records))
      .catch(
        (loadError) =>
          active &&
          setError(requestError(loadError, "Unable to load supplier bills")),
      )
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [loadBills, status]);

  const term = search.trim().toLowerCase();
  const visible = term
    ? bills.filter(
        (bill) =>
          bill.invoiceNumber.toLowerCase().includes(term) ||
          bill.supplier?.name.toLowerCase().includes(term) ||
          bill.goodsReceipt?.grnNumber.toLowerCase().includes(term),
      )
    : bills;

  return (
    <div className="p-6 text-gray-900">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Supplier Bills</h1>
          <p className="mt-1 text-sm text-gray-500">
            {visible.length} bill{visible.length === 1 ? "" : "s"} in this store
          </p>
        </div>
        <button
          type="button"
          onClick={onReceiveStock}
          className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
        >
          + Receive supplier invoice
        </button>
      </div>

      <div className="mb-6 flex flex-wrap gap-3 rounded-lg bg-white p-4 shadow">
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search invoice, supplier, or GRN..."
          className="min-w-[240px] flex-1 rounded-lg border px-4 py-2"
        />
        <select
          value={status}
          onChange={(event) =>
            setStatus(event.target.value as SupplierBillStatus | "")
          }
          className="rounded-lg border px-4 py-2"
        >
          <option value="">All statuses</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error}
        </div>
      )}
      {loading ? (
        <div className="py-12 text-center text-gray-500">
          Loading supplier bills...
        </div>
      ) : visible.length === 0 ? (
        <div className="rounded-lg bg-white py-12 text-center shadow">
          <p className="text-gray-500">No supplier bills found.</p>
          <button
            type="button"
            onClick={onReceiveStock}
            className="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-white"
          >
            Receive stock and invoice
          </button>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg bg-white shadow">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-5 py-3">Invoice</th>
                  <th className="px-5 py-3">Supplier</th>
                  <th className="px-5 py-3">GRN</th>
                  <th className="px-5 py-3">Invoice date</th>
                  <th className="px-5 py-3">Due date</th>
                  <th className="px-5 py-3 text-right">Amount</th>
                  <th className="px-5 py-3 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {visible.map((bill) => (
                  <tr
                    key={bill.id}
                    onClick={() => onOpen(bill.id)}
                    className="cursor-pointer hover:bg-gray-50"
                  >
                    <td className="px-5 py-4 font-medium text-blue-700">
                      {bill.invoiceNumber}
                    </td>
                    <td className="px-5 py-4">{bill.supplier?.name || "—"}</td>
                    <td className="px-5 py-4 text-gray-600">
                      {bill.goodsReceipt?.grnNumber || "Direct bill"}
                    </td>
                    <td className="px-5 py-4 text-gray-600">
                      {date(bill.invoiceDate)}
                    </td>
                    <td className="px-5 py-4 text-gray-600">
                      {date(bill.dueDate)}
                    </td>
                    <td className="px-5 py-4 text-right font-medium">
                      {money(bill.totalPaise)}
                    </td>
                    <td className="px-5 py-4 text-center">
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-medium ${STATUS_COLORS[bill.status]}`}
                      >
                        {STATUS_LABELS[bill.status]}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export function SupplierBillDetailView({
  billId,
  loadBill,
  postBill,
  voidBill,
  onBack,
  onOpenReceipt,
  onCreateReturn,
}: {
  billId: string;
  loadBill: (id: string) => Promise<SupplierBillRecord>;
  postBill: (id: string) => Promise<unknown>;
  voidBill: (id: string, reason: string) => Promise<unknown>;
  onBack: () => void;
  onOpenReceipt: (id: string) => void;
  onCreateReturn: (id: string) => void;
}) {
  const [bill, setBill] = useState<SupplierBillRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      setBill(await loadBill(billId));
    } catch (loadError) {
      setError(requestError(loadError, "Unable to load supplier bill"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, [billId, loadBill]);

  const post = async () => {
    setUpdating(true);
    setError(null);
    try {
      await postBill(billId);
      await refresh();
    } catch (actionError) {
      setError(requestError(actionError, "Unable to post supplier bill"));
    } finally {
      setUpdating(false);
    }
  };

  const voidInvoice = async () => {
    const reason = window.prompt("Reason for voiding this supplier bill?");
    if (!reason?.trim()) return;
    setUpdating(true);
    setError(null);
    try {
      await voidBill(billId, reason.trim());
      await refresh();
    } catch (actionError) {
      setError(requestError(actionError, "Unable to void supplier bill"));
    } finally {
      setUpdating(false);
    }
  };

  if (loading && !bill)
    return (
      <div className="p-12 text-center text-gray-500">
        Loading supplier bill...
      </div>
    );
  if (!bill)
    return (
      <div className="p-6">
        <button type="button" onClick={onBack} className="text-blue-700">
          ← Supplier bills
        </button>
        <div className="mt-6 rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
          {error || "Supplier bill not found"}
        </div>
      </div>
    );

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6 text-gray-900">
      <button
        type="button"
        onClick={onBack}
        className="text-sm font-medium text-blue-700"
      >
        ← Back to Supplier Bills
      </button>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">
            Supplier Bill #{bill.invoiceNumber}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {bill.supplier?.name || "Supplier"} · {date(bill.invoiceDate)}
          </p>
        </div>
        <span
          className={`rounded-full px-3 py-1 text-sm font-medium ${STATUS_COLORS[bill.status]}`}
        >
          {STATUS_LABELS[bill.status]}
        </span>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <section className="rounded-lg bg-white p-6 shadow">
            <h2 className="mb-4 text-lg font-semibold">Bill information</h2>
            <dl className="grid gap-4 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-gray-500">Supplier</dt>
                <dd className="font-medium">{bill.supplier?.name || "—"}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Invoice date</dt>
                <dd className="font-medium">{date(bill.invoiceDate)}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Due date</dt>
                <dd className="font-medium">{date(bill.dueDate)}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Goods receipt</dt>
                <dd>
                  {bill.goodsReceipt ? (
                    <button
                      type="button"
                      onClick={() => onOpenReceipt(bill.goodsReceipt!.id)}
                      className="font-medium text-blue-700"
                    >
                      {bill.goodsReceipt.grnNumber}
                    </button>
                  ) : (
                    <span className="font-medium">Direct bill</span>
                  )}
                </dd>
              </div>
            </dl>
          </section>

          <section className="overflow-hidden rounded-lg bg-white shadow">
            <h2 className="p-6 pb-4 text-lg font-semibold">Invoice lines</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-y bg-gray-50 text-left text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-5 py-3">Product</th>
                    <th className="px-5 py-3 text-right">Qty</th>
                    <th className="px-5 py-3 text-right">Rate</th>
                    <th className="px-5 py-3 text-right">Taxable</th>
                    <th className="px-5 py-3 text-right">GST</th>
                    <th className="px-5 py-3 text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {bill.lines.map((line) => (
                    <tr key={line.id}>
                      <td className="px-5 py-4">
                        <div className="font-medium">
                          {line.product?.name || line.sourceLineId}
                        </div>
                        <div className="text-xs text-gray-500">
                          {line.product?.sku}
                        </div>
                      </td>
                      <td className="px-5 py-4 text-right">
                        {(Number(line.quantityMilli) / 1000).toLocaleString(
                          "en-IN",
                        )}
                      </td>
                      <td className="px-5 py-4 text-right">
                        {money(line.unitCostPaise)}
                      </td>
                      <td className="px-5 py-4 text-right">
                        {money(line.taxablePaise)}
                      </td>
                      <td className="px-5 py-4 text-right">
                        {money(line.taxPaise)}
                      </td>
                      <td className="px-5 py-4 text-right font-medium">
                        {money(line.totalPaise)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        <div className="space-y-6">
          <section className="rounded-lg bg-white p-6 shadow">
            <h2 className="mb-4 text-lg font-semibold">Summary</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Subtotal</span>
                <span>{money(bill.subtotalPaise)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Discount</span>
                <span>-{money(bill.discountPaise)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">GST</span>
                <span>{money(bill.taxPaise)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Freight & handling</span>
                <span>
                  {money(
                    Number(bill.freightPaise) +
                      Number(bill.handlingPaise) +
                      Number(bill.otherChargesPaise),
                  )}
                </span>
              </div>
              <div className="flex justify-between border-t pt-3 text-base font-semibold">
                <span>Total</span>
                <span>{money(bill.totalPaise)}</span>
              </div>
            </div>
          </section>

          <section className="rounded-lg bg-white p-6 shadow">
            <h2 className="mb-4 text-lg font-semibold">Actions</h2>
            <div className="space-y-2">
              {bill.status === "DRAFT" && (
                <button
                  type="button"
                  onClick={post}
                  disabled={updating}
                  className="w-full rounded-lg bg-green-600 px-4 py-2 text-white disabled:opacity-50"
                >
                  {updating ? "Updating..." : "Post bill"}
                </button>
              )}
              {(bill.status === "DRAFT" ||
                (bill.status === "POSTED" && !bill.goodsReceiptId)) && (
                <button
                  type="button"
                  onClick={voidInvoice}
                  disabled={updating}
                  className="w-full rounded-lg border border-red-300 px-4 py-2 text-red-700 disabled:opacity-50"
                >
                  Void bill
                </button>
              )}
              {bill.status === "POSTED" && bill.goodsReceipt && (
                <button
                  type="button"
                  onClick={() => onCreateReturn(bill.goodsReceipt!.id)}
                  className="w-full rounded-lg bg-blue-600 px-4 py-2 text-white"
                >
                  Create purchase return
                </button>
              )}
              {bill.status === "POSTED" && bill.goodsReceipt && (
                <p className="text-xs text-gray-500">
                  A posted receipt-linked bill is corrected with a purchase
                  return or supplier credit note.
                </p>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
